import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { Download, Plus, ArrowLeft, Calendar, X, ChevronUp, ChevronDown, Info, TrendingUp, Pencil } from 'lucide-react';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useAuth } from '../context/AuthContext';
import { to12, formatHrsMins } from '../utils/format';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
const PAGE_SIZE = 10;

const STATUS_COLORS = {
  present: '#00c853', leave: '#fcd34d', absent: '#fca5a5', week_off: '#a5b4fc', holiday: '#c4b5fd',
};

const STATUS_SHORT = {
  present: 'P', leave: 'L', absent: 'A', week_off: 'WO', holiday: 'H',
};

export default function AttendanceDetailPage() {
  const { staffId } = useParams();
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canIncrement = hasRole('super_admin', 'organization', 'outlet_owner', 'hr_accounts');

  const [staff, setStaff] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [attendance, setAttendance] = useState([]);
  const [summary, setSummary] = useState(null);
  const [salaryData, setSalaryData] = useState(null);
  const [currentMonthSalary, setCurrentMonthSalary] = useState(null);
  const [attPage, setAttPage] = useState(1);
  const [sortCol, setSortCol] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setAttPage(1);
  };

  // Default to the current month (recomputed on each page load)
  const monthStart = startOfMonth(new Date());
  const monthEnd = endOfMonth(new Date());
  const [range, setRange] = useState([monthStart, monthEnd]);
  const [rangeStart, rangeEnd] = range;
  const [appliedFrom, setAppliedFrom] = useState(format(monthStart, 'yyyy-MM-dd'));
  const [appliedTo, setAppliedTo] = useState(format(monthEnd, 'yyyy-MM-dd'));
  const dpRef = useRef(null);

  const tableRows = React.useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const from = appliedFrom || format(monthStart, 'yyyy-MM-dd');
    const to = appliedTo && appliedTo < today ? appliedTo : today;

    const recByDate = {};
    attendance.forEach((a) => {
      const d = a.date ? a.date.split('T')[0] : null;
      if (d) recByDate[d] = a;
    });

    const rows = [];
    let cur = new Date(from + 'T00:00:00Z');
    const end = new Date(to + 'T00:00:00Z');
    while (cur <= end) {
      const dateStr = cur.toISOString().slice(0, 10);
      rows.push(recByDate[dateStr] || { _id: null, date: dateStr, timeIn: '', timeOut: '', hoursWorked: 0, status: 'leave', shortLeaves: [], fines: [] });
      cur = new Date(cur.getTime() + 24 * 60 * 60 * 1000);
    }

    rows.sort((a, b) => {
      let av, bv;
      if (sortCol === 'date') { av = a.date?.split('T')[0] || ''; bv = b.date?.split('T')[0] || ''; }
      else if (sortCol === 'timeIn') { av = a.timeIn || ''; bv = b.timeIn || ''; }
      else if (sortCol === 'timeOut') { av = a.timeOut || ''; bv = b.timeOut || ''; }
      else if (sortCol === 'hours') { av = a.hoursWorked || 0; bv = b.hoursWorked || 0; }
      else if (sortCol === 'overtime') {
        av = Math.max(0, (a.hoursWorked || 0) - (staff?.shiftHours || 0));
        bv = Math.max(0, (b.hoursWorked || 0) - (staff?.shiftHours || 0));
      }
      else if (sortCol === 'shortleave') { av = a.shortLeaves?.length || 0; bv = b.shortLeaves?.length || 0; }
      else if (sortCol === 'fine') {
        av = (a.fines || []).reduce((s, f) => s + (f.amount || 0), 0);
        bv = (b.fines || []).reduce((s, f) => s + (f.amount || 0), 0);
      }
      else if (sortCol === 'status') { av = a.status || ''; bv = b.status || ''; }
      else { av = a.date?.split('T')[0] || ''; bv = b.date?.split('T')[0] || ''; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return rows;
  }, [attendance, appliedFrom, appliedTo, sortCol, sortDir, staff, monthStart]);

  const [showMarkModal, setShowMarkModal] = useState(false);
  const [markForm, setMarkForm] = useState({ date: format(new Date(), 'yyyy-MM-dd'), timeIn: '', timeOut: '', status: 'present' });

  const [showShortLeaveModal, setShowShortLeaveModal] = useState(false);
  const [slForm, setSlForm] = useState({ date: format(new Date(), 'yyyy-MM-dd'), leaves: [{ timeOut: '', timeIn: '' }] });

  const [showFineModal, setShowFineModal] = useState(false);
  const [fineForm, setFineForm] = useState({ date: format(new Date(), 'yyyy-MM-dd'), fines: [{ amount: '', reason: '' }] });

  const [showAdvanceModal, setShowAdvanceModal] = useState(false);
  const [advanceForm, setAdvanceForm] = useState({ date: format(new Date(), 'yyyy-MM-dd'), amount: '', note: '' });

  const [showIncrementModal, setShowIncrementModal] = useState(false);
  const [incrementForm, setIncrementForm] = useState({ date: format(new Date(), 'yyyy-MM-dd'), incrementAmount: '' });

  // Load staff record + current month salary
  useEffect(() => {
    let active = true;
    api.get(`/staff/${staffId}`)
      .then((r) => { if (active) setStaff(r.data); })
      .catch((err) => {
        if (!active) return;
        const msg = err.response?.data?.message || err.message || 'Unable to load staff';
        const status = err.response?.status;
        setAccessError(status === 403 ? 'Access denied — you do not have permission to view this staff member.' : msg);
        setNotFound(true);
      });

    const mFrom = format(startOfMonth(new Date()), 'yyyy-MM-dd');
    const mTo = format(endOfMonth(new Date()), 'yyyy-MM-dd');
    api.get(`/salary/calculate?staffId=${staffId}&from=${mFrom}&to=${mTo}`)
      .then((r) => { if (active) setCurrentMonthSalary(r.data); })
      .catch(() => {});

    return () => { active = false; };
  }, [staffId]);

  const applyRange = () => {
    setAppliedFrom(rangeStart ? format(rangeStart, 'yyyy-MM-dd') : '');
    setAppliedTo(rangeEnd ? format(rangeEnd, 'yyyy-MM-dd') : '');
    dpRef.current?.setOpen(false);
  };

  const fetchAttendance = useCallback(async () => {
    if (!staffId) return;
    const params = new URLSearchParams({ staffId });
    if (appliedFrom) params.append('from', appliedFrom);
    if (appliedTo) params.append('to', appliedTo);
    const attRes = await api.get(`/attendance?${params.toString()}`);
    setAttendance(attRes.data);
    setAttPage(1);

    const sFrom = appliedFrom || '2000-01-01';
    const sTo = appliedTo || format(new Date(), 'yyyy-MM-dd');

    const mFrom = format(startOfMonth(new Date()), 'yyyy-MM-dd');
    const mTo = format(endOfMonth(new Date()), 'yyyy-MM-dd');

    const [sumRes, salRes, cmsSalRes] = await Promise.all([
      api.get(`/attendance/summary?staffId=${staffId}&from=${sFrom}&to=${sTo}`),
      api.get(`/salary/calculate?staffId=${staffId}&from=${sFrom}&to=${sTo}`),
      api.get(`/salary/calculate?staffId=${staffId}&from=${mFrom}&to=${mTo}`),
    ]);
    setSummary(sumRes.data);
    setSalaryData(salRes.data);
    setCurrentMonthSalary(cmsSalRes.data);
  }, [staffId, appliedFrom, appliedTo]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  const handleMarkAttendance = async (e) => {
    e.preventDefault();
    try {
      await api.post('/attendance', {
        staff: staffId,
        outlet: staff?.outlet?._id,
        ...markForm,
      });
      toast.success('Attendance marked');
      setShowMarkModal(false);
      fetchAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const handleAddAdvance = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/staff/${staffId}/advance`, advanceForm);
      toast.success('Advance salary recorded');
      setShowAdvanceModal(false);
      setAdvanceForm({ date: format(new Date(), 'yyyy-MM-dd'), amount: '', note: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const handleAddIncrement = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/staff/${staffId}/increment`, incrementForm);
      toast.success('Increment added');
      setShowIncrementModal(false);
      setIncrementForm({ date: format(new Date(), 'yyyy-MM-dd'), incrementAmount: '' });
      const r = await api.get(`/staff/${staffId}`);
      setStaff(r.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  // Find existing attendance record for a given yyyy-MM-dd date
  const recordForDate = (dateStr) =>
    attendance.find((a) => a.date?.split('T')[0] === dateStr);

  const openShortLeave = (record) => {
    if (record) {
      const dateStr = record.date.split('T')[0];
      const leaves = (record.shortLeaves && record.shortLeaves.length)
        ? record.shortLeaves.map((s) => ({ timeOut: s.timeOut || '', timeIn: s.timeIn || '' }))
        : [{ timeOut: '', timeIn: '' }];
      setSlForm({ date: dateStr, leaves });
    } else {
      setSlForm({ date: format(new Date(), 'yyyy-MM-dd'), leaves: [{ timeOut: '', timeIn: '' }] });
    }
    setShowShortLeaveModal(true);
  };

  // When the date changes inside the modal, prefill existing short leaves for it
  const onSlDateChange = (dateStr) => {
    const rec = recordForDate(dateStr);
    const leaves = (rec && rec.shortLeaves && rec.shortLeaves.length)
      ? rec.shortLeaves.map((s) => ({ timeOut: s.timeOut || '', timeIn: s.timeIn || '' }))
      : [{ timeOut: '', timeIn: '' }];
    setSlForm({ date: dateStr, leaves });
  };

  const addSlRow = () => {
    if (slForm.leaves.length >= 3) { toast.error('Maximum 3 short leaves per day'); return; }
    setSlForm({ ...slForm, leaves: [...slForm.leaves, { timeOut: '', timeIn: '' }] });
  };
  const removeSlRow = (i) => {
    const row = slForm.leaves[i];
    const hasData = row && (row.timeOut || row.timeIn);
    if (hasData && !window.confirm('Delete this short leave?')) return;
    setSlForm({ ...slForm, leaves: slForm.leaves.filter((_, idx) => idx !== i) });
  };
  const updateSlRow = (i, field, value) => {
    const leaves = slForm.leaves.map((l, idx) => idx === i ? { ...l, [field]: value } : l);
    setSlForm({ ...slForm, leaves });
  };

  const handleSaveShortLeave = async (e) => {
    e.preventDefault();
    const cleaned = slForm.leaves.filter((l) => l.timeOut || l.timeIn);
    for (const l of cleaned) {
      if (!l.timeOut || !l.timeIn) { toast.error('Each short leave needs both Time Out and Time In'); return; }
    }
    if (cleaned.length > 3) { toast.error('Maximum 3 short leaves per day'); return; }
    try {
      await api.post('/attendance/short-leave', {
        staff: staffId,
        outlet: staff?.outlet?._id,
        date: slForm.date,
        shortLeaves: cleaned,
      });
      toast.success('Short leave saved');
      setShowShortLeaveModal(false);
      fetchAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  // ---- Fines ----
  const openFine = (record) => {
    if (record) {
      const dateStr = record.date.split('T')[0];
      const fines = (record.fines && record.fines.length)
        ? record.fines.map((f) => ({ amount: f.amount || '', reason: f.reason || '' }))
        : [{ amount: '', reason: '' }];
      setFineForm({ date: dateStr, fines });
    } else {
      setFineForm({ date: format(new Date(), 'yyyy-MM-dd'), fines: [{ amount: '', reason: '' }] });
    }
    setShowFineModal(true);
  };

  const onFineDateChange = (dateStr) => {
    const rec = recordForDate(dateStr);
    const fines = (rec && rec.fines && rec.fines.length)
      ? rec.fines.map((f) => ({ amount: f.amount || '', reason: f.reason || '' }))
      : [{ amount: '', reason: '' }];
    setFineForm({ date: dateStr, fines });
  };

  const addFineRow = () => {
    if (fineForm.fines.length >= 3) { toast.error('Maximum 3 fines per day'); return; }
    setFineForm({ ...fineForm, fines: [...fineForm.fines, { amount: '', reason: '' }] });
  };
  const removeFineRow = (i) => {
    const row = fineForm.fines[i];
    const hasData = row && (row.amount || row.reason);
    if (hasData && !window.confirm('Delete this fine?')) return;
    setFineForm({ ...fineForm, fines: fineForm.fines.filter((_, idx) => idx !== i) });
  };
  const updateFineRow = (i, field, value) => {
    const fines = fineForm.fines.map((f, idx) => idx === i ? { ...f, [field]: value } : f);
    setFineForm({ ...fineForm, fines });
  };

  const handleSaveFine = async (e) => {
    e.preventDefault();
    const cleaned = fineForm.fines
      .map((f) => ({ amount: Number(f.amount) || 0, reason: f.reason || '' }))
      .filter((f) => f.amount > 0);
    if (cleaned.length > 3) { toast.error('Maximum 3 fines per day'); return; }
    try {
      await api.post('/attendance/fine', {
        staff: staffId,
        outlet: staff?.outlet?._id,
        date: fineForm.date,
        fines: cleaned,
      });
      toast.success('Fine saved');
      setShowFineModal(false);
      fetchAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error');
    }
  };

  const downloadSlip = () => {
    if (!salaryData) return;
    const s = salaryData.staff;
    const doc = new jsPDF();
    const navy = [13, 59, 102];
    const white = [255, 255, 255];
    const light = [228, 237, 246];

    // Header bar
    doc.setFillColor(...navy);
    doc.rect(0, 0, 210, 28, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(18); doc.setFont('helvetica', 'bold');
    doc.text('CloudStay', 14, 12);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('Salary Slip', 14, 21);
    doc.text(`Period: ${salaryData.period.from} to ${salaryData.period.to}`, 105, 21, { align: 'center' });

    // Staff info
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(13); doc.setFont('helvetica', 'bold');
    doc.text(s.name, 14, 40);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 116, 139);
    doc.text(`${s.staffId}`, 14, 47);

    // Divider
    doc.setDrawColor(...light); doc.setLineWidth(0.5);
    doc.line(14, 52, 196, 52);

    // Rows helper
    const row = (label, value, y, shade) => {
      if (shade) { doc.setFillColor(...light); doc.rect(14, y - 5, 182, 9, 'F'); }
      doc.setTextColor(100, 116, 139); doc.setFontSize(10); doc.setFont('helvetica', 'normal');
      doc.text(label, 16, y);
      doc.setTextColor(15, 23, 42); doc.setFont('helvetica', 'bold');
      doc.text(String(value), 130, y);
    };

    let y = 62;
    row('Monthly Salary', `Rs ${s.salary?.toLocaleString()}`, y, false); y += 12;
    row('Shift Hours', `${salaryData.shiftHours} hrs`, y, true); y += 12;
    row('Per Day Rate', `Rs ${salaryData.perDaySalary}`, y, false); y += 12;
    row('Per Hour Rate', `Rs ${salaryData.perHourSalary}`, y, true); y += 12;
    row('Days Present', salaryData.totalPresent, y, false); y += 12;
    row('Total Hours Worked', `${salaryData.totalHoursWorked} hrs`, y, true); y += 12;
    row('Overtime Hours', `${salaryData.totalOvertimeHours} hrs`, y, false); y += 12;
    row('Leave Days', salaryData.totalLeave, y, true); y += 12;
    row('Absent Days', salaryData.totalAbsent, y, false); y += 12;
    row('Absent Penalty', `- Rs ${salaryData.absentPenalty?.toLocaleString()}`, y, true); y += 12;
    row('Fines', `- Rs ${(salaryData.totalFines || 0).toLocaleString()}`, y, false); y += 12;
    row('Advance Salary', `- Rs ${(salaryData.totalAdvance || 0).toLocaleString()}`, y, true); y += 18;

    // Net Salary box
    doc.setFillColor(...navy);
    doc.roundedRect(14, y, 182, 18, 3, 3, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(12); doc.setFont('helvetica', 'bold');
    doc.text('Net Salary', 20, y + 12);
    doc.setFontSize(14);
    doc.text(`Rs ${salaryData.netSalary?.toLocaleString()}`, 192, y + 12, { align: 'right' });

    doc.save(`salary_slip_${s.name}_${salaryData.period.from}.pdf`);
  };

  const downloadCSV = () => {
    const rows = attendance.map((a) => {
      const h = a.hoursWorked || 0;
      const sh = staff?.shiftHours || 0;
      const hrs = sh > 0 ? Math.min(h, sh) : h;
      const ot = Math.max(0, h - sh);
      const [y, m, d] = (a.date?.split('T')[0] || '').split('-');
      return {
        Date: d && m && y ? `${d}-${m}-${y.slice(2)}` : '',
        'Time In': to12(a.timeIn) || '',
        'Time Out': to12(a.timeOut) || '',
        Hours: hrs > 0 ? `${Math.round(hrs * 100) / 100} hrs` : '',
        Overtime: ot > 0 ? `${Math.round(ot * 100) / 100} hrs` : '',
        Status: (a.status || '').replace('_', ' '),
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
    XLSX.writeFile(wb, `attendance_${staff?.name}_${appliedFrom || 'all'}.xlsx`);
  };

  if (notFound) {
    return (
      <div className="page">
        <div className="empty-state">
          <div style={{ marginBottom: 8, fontWeight: 600 }}>Could not load staff member</div>
          {accessError && (
            <div style={{ fontSize: 13, color: '#ef4444', marginBottom: 12, maxWidth: 420 }}>{accessError}</div>
          )}
          <button className="btn btn-outline" onClick={() => navigate('/attendance')}>Back to Attendance</button>
        </div>
      </div>
    );
  }

  if (!staff) {
    return <div className="page"><div className="loading-screen">Loading...</div></div>;
  }

  const totalPages = Math.ceil(tableRows.length / PAGE_SIZE);

  return (
    <div className="page">
      {/* 60-40 Layout — full page including header */}
      <div className="att-split-layout">

        {/* LEFT — 60% — Header + Controls + Attendance Records */}
        <div className="att-split-left">
          <div className="page-header">
            <div className="staff-header-card" style={{ border: 'none', padding: 0, margin: 0, background: 'transparent' }}>
              <button className="back-arrow-btn" onClick={() => navigate('/attendance')} title="Back">
                <ArrowLeft size={22} />
              </button>
              {staff.photo && <img src={`${API_BASE}/uploads/${staff.photo}`} alt="" className="staff-photo-sm" />}
              <div>
                <h2 style={{ fontSize: 20 }}>{staff.name}</h2>
                <p className="text-muted">{staff.staffId} · {staff.outlet?.name}</p>
              </div>
            </div>
            <div className="header-actions">
              <button className="btn btn-primary" onClick={() => { setMarkForm({ date: format(new Date(), 'yyyy-MM-dd'), timeIn: '', timeOut: '', status: 'present' }); setShowMarkModal(true); }}>
                <Plus size={16} /> Mark Attendance
              </button>
              <button className="btn btn-primary" onClick={() => { setAdvanceForm({ date: format(new Date(), 'yyyy-MM-dd'), amount: '', note: '' }); setShowAdvanceModal(true); }}>
                <Plus size={16} /> Advance Salary
              </button>
              {canIncrement && (
                <button className="btn btn-primary" onClick={() => { setIncrementForm({ date: format(new Date(), 'yyyy-MM-dd'), incrementAmount: '' }); setShowIncrementModal(true); }}>
                  <TrendingUp size={16} /> Add Increment
                </button>
              )}
              {attendance.length > 0 && (
                <button className="btn btn-outline btn-icon" onClick={downloadCSV} title="Download attendance (Excel)">
                  <Download size={16} />
                </button>
              )}
            </div>
          </div>
          <div className="header-actions" style={{ justifyContent: 'flex-end', marginBottom: 16 }}>
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <DatePicker
                ref={dpRef}
                showIcon
                icon={<Calendar size={16} />}
                selectsRange
                startDate={rangeStart}
                endDate={rangeEnd}
                onChange={(update) => {
                  const u = update || [null, null];
                  setRange(u);
                }}
                shouldCloseOnSelect={false}
                dateFormat="dd MMM yyyy"
                placeholderText="Select date range"
                className="form-input datepicker-input"
                popperPlacement="bottom-end"
                popperProps={{ strategy: 'fixed' }}
              >
                <div className="datepicker-footer">
                  <button type="button" className="btn btn-sm btn-primary" onClick={applyRange}>OK</button>
                </div>
              </DatePicker>
              {(appliedFrom !== format(monthStart, 'yyyy-MM-dd') || appliedTo !== format(monthEnd, 'yyyy-MM-dd')) && (
                <button
                  type="button"
                  className="btn btn-sm btn-icon"
                  title="Reset to current month"
                  style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', padding: 2 }}
                  onClick={() => {
                    setRange([monthStart, monthEnd]);
                    setAppliedFrom(format(monthStart, 'yyyy-MM-dd'));
                    setAppliedTo(format(monthEnd, 'yyyy-MM-dd'));
                  }}
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="table-container att-table-scroll">
            <table className="table" style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '15%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '14%' }} />
              </colgroup>
              <thead>
                <tr>
                  {[['date','Date'],['timeIn','Time In'],['timeOut','Time Out'],['hours','Hours'],['overtime','Overtime'],['shortleave','Short Leave'],['fine','Fine'],['status','Status']].map(([col, label]) => (
                    <th key={col} className="th-sortable" onClick={() => handleSort(col)}>
                      <span className="th-sort-inner">
                        {label}
                        <span className="sort-arrows">
                          <ChevronUp size={11} className={sortCol === col && sortDir === 'asc' ? 'active' : ''} />
                          <ChevronDown size={11} className={sortCol === col && sortDir === 'desc' ? 'active' : ''} />
                        </span>
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.slice((attPage - 1) * PAGE_SIZE, attPage * PAGE_SIZE).map((a) => {
                  const isLeave = !a.timeIn && !a.timeOut;
                  const dimStyle = isLeave ? { color: '#9ca3af' } : {};
                  return (
                  <tr key={a._id || a.date}>
                    <td style={dimStyle}>{a.date ? (() => { const [y,m,d] = a.date.split('T')[0].split('-'); return `${d}-${m}-${y.slice(2)}`; })() : '—'}</td>
                    <td>{to12(a.timeIn) || '—'}</td>
                    <td>{to12(a.timeOut) || '—'}</td>
                    <td>{(() => {
                      if (!a.timeIn || !a.timeOut) return '—';
                      const h = a.hoursWorked || 0;
                      const sh = staff?.shiftHours || 0;
                      const hrs = sh > 0 ? Math.min(h, sh) : h;
                      return hrs > 0 ? formatHrsMins(hrs) : '—';
                    })()}</td>
                    <td>{(() => {
                      if (!a.timeIn || !a.timeOut) return '—';
                      const ot = Math.max(0, (a.hoursWorked || 0) - (staff?.shiftHours || 0));
                      return ot > 0 ? formatHrsMins(ot) : '—';
                    })()}</td>
                    <td>
                      <button className="short-leave-cell" onClick={() => openShortLeave(a)} title="View / edit short leaves">
                        {a.shortLeaves && a.shortLeaves.length ? (
                          <span className="sl-cell-inner">
                            <span className="sl-badge">{a.shortLeaves.length}</span>
                            <span className="sl-hours">{(() => {
                              const mins = a.shortLeaves.reduce((sum, sl) => {
                                if (!sl.timeOut || !sl.timeIn) return sum;
                                const [oh, om] = sl.timeOut.split(':').map(Number);
                                const [ih, im] = sl.timeIn.split(':').map(Number);
                                let d = (ih * 60 + im) - (oh * 60 + om);
                                if (d < 0) d += 24 * 60;
                                return sum + d;
                              }, 0);
                              return `${Math.round((mins / 60) * 100) / 100} hrs`;
                            })()}</span>
                          </span>
                        ) : <span className="sl-add">+ Add</span>}
                      </button>
                    </td>
                    <td>
                      <button className="short-leave-cell" onClick={() => openFine(a)} title="View / edit fines">
                        {a.fines && a.fines.length ? (
                          <span className="sl-cell-inner">
                            <span className="sl-badge fine-badge">{a.fines.length}</span>
                            <span className="sl-hours">₹{a.fines.reduce((s, f) => s + (f.amount || 0), 0).toLocaleString()}</span>
                          </span>
                        ) : <span className="sl-add">+ Add</span>}
                      </button>
                    </td>
                    <td style={dimStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {(() => {
                          const explicit = a.status === 'absent' || a.status === 'week_off' || a.status === 'holiday';
                          const s = explicit ? a.status : (!a.timeIn && !a.timeOut) ? 'leave' : (a.status || 'leave');
                          return (<>
                            <span className="status-dot" style={{ background: STATUS_COLORS[s] }} />
                            <span className="status-text" title={s.replace('_', ' ')}>{STATUS_SHORT[s] || '—'}</span>
                          </>);
                        })()}
                        <button
                          type="button"
                          className="btn btn-sm btn-outline btn-icon"
                          title="Edit attendance"
                          onClick={() => {
                            const explicit = a.status === 'absent' || a.status === 'week_off' || a.status === 'holiday';
                            const effectiveStatus = explicit ? a.status : (!a.timeIn && !a.timeOut) ? 'leave' : (a.status || 'present');
                            setMarkForm({ date: a.date.split('T')[0], timeIn: a.timeIn || '', timeOut: a.timeOut || '', status: effectiveStatus });
                            setShowMarkModal(true);
                          }}
                        >
                          <Pencil size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
                {tableRows.length === 0 && (
                  <tr><td colSpan={8} className="empty-state">No dates in the selected period.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {tableRows.length > PAGE_SIZE && (
            <div className="pagination">
              <span className="pagination-info">
                Showing {Math.min((attPage - 1) * PAGE_SIZE + 1, tableRows.length)}–{Math.min(attPage * PAGE_SIZE, tableRows.length)} of {tableRows.length}
              </span>
              <div className="pagination-controls">
                <button className="btn btn-sm btn-outline" onClick={() => setAttPage(1)} disabled={attPage === 1}>«</button>
                <button className="btn btn-sm btn-outline" onClick={() => setAttPage((p) => p - 1)} disabled={attPage === 1}>‹ Prev</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - attPage) <= 2)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, idx) => p === '...'
                    ? <span key={`e${idx}`} className="pagination-ellipsis">…</span>
                    : <button key={p} className={`btn btn-sm ${attPage === p ? 'btn-primary' : 'btn-outline'}`} onClick={() => setAttPage(p)}>{p}</button>
                  )}
                <button className="btn btn-sm btn-outline" onClick={() => setAttPage((p) => p + 1)} disabled={attPage === totalPages}>Next ›</button>
                <button className="btn btn-sm btn-outline" onClick={() => setAttPage(totalPages)} disabled={attPage === totalPages}>»</button>
              </div>
            </div>
          )}

          <div className="salary-note" style={{ marginTop: 16 }}>
            <strong>Formula:</strong> Per-hour rate = Salary ÷ 26 ÷ Shift Hours.
            Earned = (hours worked − short-leave hours) × per-hour rate.
            Short leave is unpaid (deducted at the per-hour rate).
            Absent penalty = absent days × 2 × per-day rate.
            Fines are flat deductions. Net = Earned − Absent penalty − Fines.
          </div>
        </div>

        {/* RIGHT — 40% — Salary Summary */}
        <div className="att-split-right">
          {salaryData && (
        <div className="salary-result">
          <div className="salary-header">
            <div>
              <h3>Salary Summary</h3>
              <p className="text-muted">Period: {salaryData.period.from} to {salaryData.period.to}</p>
            </div>
            <button className="btn btn-outline btn-icon" onClick={downloadSlip} title="Download salary slip (PDF)">
              <Download size={16} />
            </button>
          </div>

          <div className="salary-cards">
            {[
              { label: 'Monthly Salary', value: `₹${salaryData.staff.salary?.toLocaleString()}` },
              { label: 'Shift Hours', value: `${salaryData.shiftHours} hrs` },
              { label: 'Per Day Rate', value: `₹${salaryData.perDaySalary}` },
              { label: 'Per Hour Rate', value: `₹${salaryData.perHourSalary}` },
              { label: 'Days Present', value: salaryData.totalPresent },
              { label: 'Total Hours', value: `${salaryData.totalHoursWorked} hrs` },
              { label: 'Overtime', value: `${salaryData.totalOvertimeHours} hrs` },
              { label: 'Leave Days', value: salaryData.totalLeave },
              { label: 'Absent Days', value: salaryData.totalAbsent },
              { label: 'Short Leave Hrs', value: `${salaryData.totalShortLeaveHours || 0} hrs` },
              { label: 'Short Leave Deduction', value: `− ₹${(salaryData.shortLeaveDeduction || 0).toLocaleString()}` },
              { label: 'Fines', value: `− ₹${(salaryData.totalFines || 0).toLocaleString()}` },
              { label: 'Absent Penalty', value: `− ₹${salaryData.absentPenalty?.toLocaleString()}` },
              {
                label: 'Advance Salary',
                value: `− ₹${(salaryData.totalAdvance || 0).toLocaleString()}`,
                tooltip: (staff.advanceHistory || [])
                  .filter((a) => {
                    const d = a.date?.split('T')[0];
                    return d && (!appliedFrom || d >= appliedFrom) && (!appliedTo || d <= appliedTo);
                  })
                  .sort((a, b) => (a.date < b.date ? -1 : 1)),
              },
            ].map(({ label, value, tooltip }) => (
              <div key={label} className={`summary-card${tooltip ? ' has-tooltip' : ''}`}>
                <p className="summary-value">{value}</p>
                <p className="summary-label">
                  {label}
                  {tooltip && <Info size={13} className="card-info-icon" />}
                </p>
                {tooltip && tooltip.length > 0 && (
                  <div className="card-tooltip">
                    {tooltip.map((a, i) => (
                      <div key={i} className="card-tooltip-row">
                        <span>{a.date?.split('T')[0]}</span>
                        <span>₹{(a.amount || 0).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="net-salary-card">
            <span>Net Salary</span>
            <span className="net-amount">₹{salaryData.netSalary?.toLocaleString()}</span>
          </div>

          </div>
        )}
        </div>{/* end att-split-right */}

      </div>{/* end att-split-layout */}

      {/* Mark Attendance Modal */}
      {showMarkModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>Mark Attendance – {staff.name}</h3>
              <button className="btn btn-sm btn-outline btn-icon" onClick={() => setShowMarkModal(false)} title="Close">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleMarkAttendance}>
              <div className="form-group">
                <label>Date *</label>
                <input type="date" value={markForm.date} onChange={(e) => setMarkForm({ ...markForm, date: e.target.value })} onClick={(e) => e.target.showPicker?.()} style={{ cursor: 'pointer' }} required />
              </div>
              <div className="form-group">
                <label>Status *</label>
                <select value={markForm.status} onChange={(e) => {
                  const s = e.target.value;
                  setMarkForm({ ...markForm, status: s, ...(s !== 'present' ? { timeIn: '', timeOut: '' } : {}) });
                }}>
                  <option value="present">Present</option>
                  <option value="leave">Leave</option>
                  <option value="absent">Absent</option>
                  <option value="holiday">Holiday</option>
                </select>
              </div>
              {markForm.status === 'present' && (
                <>
                  <div className="form-group">
                    <label>Time In</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="time" value={markForm.timeIn} onChange={(e) => setMarkForm({ ...markForm, timeIn: e.target.value })} onClick={(e) => e.target.showPicker?.()} style={{ cursor: 'pointer', flex: 1 }} />
                      <button type="button" className="btn btn-sm btn-outline btn-icon" onClick={() => setMarkForm({ ...markForm, timeIn: '' })} title="Clear time in"><X size={13} /></button>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Time Out</label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input type="time" value={markForm.timeOut} onChange={(e) => setMarkForm({ ...markForm, timeOut: e.target.value })} onClick={(e) => e.target.showPicker?.()} style={{ cursor: 'pointer', flex: 1 }} />
                      <button type="button" className="btn btn-sm btn-outline btn-icon" onClick={() => setMarkForm({ ...markForm, timeOut: '' })} title="Clear time out"><X size={13} /></button>
                    </div>
                  </div>
                </>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowMarkModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Advance Salary Modal */}
      {showAdvanceModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>Advance Salary – {staff.name}</h3>
              <button className="btn btn-sm btn-outline btn-icon" onClick={() => setShowAdvanceModal(false)} title="Close">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleAddAdvance}>
              <div className="form-group">
                <label>Date *</label>
                <input type="date" value={advanceForm.date} onChange={(e) => setAdvanceForm({ ...advanceForm, date: e.target.value })} onClick={(e) => e.target.showPicker?.()} style={{ cursor: 'pointer' }} required />
              </div>
              <div className="form-group">
                <label>Amount (₹) *</label>
                <input type="number" min="1" value={advanceForm.amount} onChange={(e) => setAdvanceForm({ ...advanceForm, amount: e.target.value })} placeholder="e.g. 2000" required />
              </div>
              <div className="form-group">
                <label>Note</label>
                <input type="text" value={advanceForm.note} onChange={(e) => setAdvanceForm({ ...advanceForm, note: e.target.value })} placeholder="Reason / remark (optional)" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowAdvanceModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Increment Modal */}
      {showIncrementModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>Salary Increment – {staff.name}</h3>
              <button className="btn btn-sm btn-outline btn-icon" onClick={() => setShowIncrementModal(false)} title="Close">
                <X size={16} />
              </button>
            </div>
            <p className="text-muted">Current salary: ₹{staff.salary?.toLocaleString()}</p>
            <form onSubmit={handleAddIncrement}>
              <div className="form-group">
                <label>Effective Date *</label>
                <input type="date" value={incrementForm.date} onChange={(e) => setIncrementForm({ ...incrementForm, date: e.target.value })} onClick={(e) => e.target.showPicker?.()} style={{ cursor: 'pointer' }} required />
              </div>
              <div className="form-group">
                <label>Increment Amount (₹) *</label>
                <input type="number" value={incrementForm.incrementAmount} onChange={(e) => setIncrementForm({ ...incrementForm, incrementAmount: e.target.value })} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowIncrementModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Apply Increment</button>
              </div>
            </form>
            {staff.incrementHistory?.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ margin: '0 0 8px' }}>Increment History</h4>
                <table className="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Increment Amount</th>
                      <th>Total Salary</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...staff.incrementHistory]
                      .sort((a, b) => new Date(b.date) - new Date(a.date))
                      .map((inc, i) => (
                        <tr key={i}>
                          <td>{inc.date?.split('T')[0]}</td>
                          <td>₹{(inc.newSalary - inc.oldSalary)?.toLocaleString()}</td>
                          <td>₹{inc.newSalary?.toLocaleString()}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Short Leave Modal */}
      {showShortLeaveModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>Short Leave – {staff.name}</h3>
              <button className="btn btn-sm btn-outline btn-icon" onClick={() => setShowShortLeaveModal(false)} title="Close">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleSaveShortLeave}>
              <div className="form-group">
                <label>Date *</label>
                <input type="date" value={slForm.date} onChange={(e) => onSlDateChange(e.target.value)} onClick={(e) => e.target.showPicker?.()} style={{ cursor: 'pointer' }} required />
              </div>

              <label className="sl-section-label">Short Leaves <span className="text-muted">(max 3 per day)</span></label>

              {slForm.leaves.length === 0 && (
                <p className="text-muted" style={{ margin: '4px 0 8px' }}>No short leaves. Saving will clear all short leaves for this date.</p>
              )}

              {slForm.leaves.map((l, i) => (
                <div key={i} className="sl-row">
                  <div className="sl-field">
                    <label>Time Out</label>
                    <input type="time" value={l.timeOut} onChange={(e) => updateSlRow(i, 'timeOut', e.target.value)} onClick={(e) => e.target.showPicker?.()} style={{ cursor: 'pointer' }} />
                  </div>
                  <div className="sl-field">
                    <label>Time In</label>
                    <input type="time" value={l.timeIn} onChange={(e) => updateSlRow(i, 'timeIn', e.target.value)} onClick={(e) => e.target.showPicker?.()} style={{ cursor: 'pointer' }} />
                  </div>
                  <button type="button" className="btn btn-sm btn-outline btn-icon sl-remove" onClick={() => removeSlRow(i)} title="Remove this short leave">
                    <X size={14} />
                  </button>
                </div>
              ))}

              {slForm.leaves.length < 3 && (
                <button type="button" className="btn btn-sm btn-outline" style={{ marginTop: 8 }} onClick={addSlRow}>
                  <Plus size={14} /> Add Short Leave
                </button>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowShortLeaveModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fine Modal */}
      {showFineModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header-row">
              <h3>Fine – {staff.name}</h3>
              <button className="btn btn-sm btn-outline btn-icon" onClick={() => setShowFineModal(false)} title="Close">
                <X size={16} />
              </button>
            </div>
            <form onSubmit={handleSaveFine}>
              <div className="form-group">
                <label>Date *</label>
                <input type="date" value={fineForm.date} onChange={(e) => onFineDateChange(e.target.value)} onClick={(e) => e.target.showPicker?.()} style={{ cursor: 'pointer' }} required />
              </div>

              <label className="sl-section-label">Fines <span className="text-muted">(max 3 per day)</span></label>

              {fineForm.fines.length === 0 && (
                <p className="text-muted" style={{ margin: '4px 0 8px' }}>No fines. Saving will clear all fines for this date.</p>
              )}

              {fineForm.fines.map((f, i) => (
                <div key={i} className="sl-row">
                  <div className="sl-field">
                    <label>Amount (₹)</label>
                    <input type="number" min="0" value={f.amount} onChange={(e) => updateFineRow(i, 'amount', e.target.value)} placeholder="0" />
                  </div>
                  <div className="sl-field" style={{ flex: 2 }}>
                    <label>Reason</label>
                    <input type="text" value={f.reason} onChange={(e) => updateFineRow(i, 'reason', e.target.value)} placeholder="e.g. Late, breakage" />
                  </div>
                  <button type="button" className="btn btn-sm btn-outline btn-icon sl-remove" onClick={() => removeFineRow(i)} title="Remove this fine">
                    <X size={14} />
                  </button>
                </div>
              ))}

              {fineForm.fines.length < 3 && (
                <button type="button" className="btn btn-sm btn-outline" style={{ marginTop: 8 }} onClick={addFineRow}>
                  <Plus size={14} /> Add Fine
                </button>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowFineModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
